const SITE_NAME = "Nexovia IT"

/**
 * Builds the title/description/canonical/OG/Twitter meta bundle a
 * TanStack Router route's `head()` returns, so every public page gets the
 * same baseline SEO tags instead of each route hand-rolling a subset.
 *
 * `path` is the route's own path (e.g. "/about") — routes here are static
 * per file, so it's just written at the call site rather than derived.
 * `image`, when given, should be an absolute URL (social platforms don't
 * reliably resolve relative og:image paths).
 */
export function pageMeta({
  title,
  description,
  path,
  image,
}: {
  title: string
  description: string
  path: string
  image?: string
}) {
  const origin = typeof window !== "undefined" ? window.location.origin : ""
  const url = `${origin}${path}`

  const meta: { title?: string; name?: string; property?: string; content?: string }[] = [
    { title },
    { name: "description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:site_name", content: SITE_NAME },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:url", content: url },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
  ]

  if (image) {
    meta.push(
      { property: "og:image", content: image },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: image },
    )
  } else {
    meta.push({ name: "twitter:card", content: "summary" })
  }

  return {
    meta,
    links: [{ rel: "canonical", href: url }],
  }
}

/**
 * Imperatively syncs <title> and the OG/Twitter/description meta tags to
 * freshly-fetched data (a blog post's real title/excerpt/cover image).
 *
 * Why not just `pageMeta()` in the route's `head()`: this app fetches page
 * content client-side with react-query rather than through a router
 * loader, so `head()` only ever sees the route's static params (e.g. the
 * slug) — never the post itself. `head()` sets a reasonable placeholder
 * immediately; call this once the real record loads to correct it in
 * place. Tags not already present (this page's first visit in the SPA
 * session) are created; tags `pageMeta` already rendered are updated
 * rather than duplicated.
 */
export function syncMetaTags({
  title,
  description,
  image,
}: {
  title: string
  description?: string
  image?: string
}) {
  if (typeof document === "undefined") return

  document.title = title

  const setMeta = (selector: string, attrs: Record<string, string>, content: string) => {
    let el = document.querySelector<HTMLMetaElement>(selector)
    if (!el) {
      el = document.createElement("meta")
      for (const [key, value] of Object.entries(attrs)) el.setAttribute(key, value)
      document.head.appendChild(el)
    }
    el.setAttribute("content", content)
  }

  if (description) {
    setMeta('meta[name="description"]', { name: "description" }, description)
    setMeta('meta[name="twitter:description"]', { name: "twitter:description" }, description)
    setMeta('meta[property="og:description"]', { property: "og:description" }, description)
  }
  setMeta('meta[property="og:title"]', { property: "og:title" }, title)
  setMeta('meta[name="twitter:title"]', { name: "twitter:title" }, title)

  if (image) {
    setMeta('meta[property="og:image"]', { property: "og:image" }, image)
    setMeta('meta[name="twitter:image"]', { name: "twitter:image" }, image)
    setMeta('meta[name="twitter:card"]', { name: "twitter:card" }, "summary_large_image")
  }
}
